/**
 * Trainer session recording via lkapi → Streaming Engine → LiveKit egress.
 *
 * Capture: RoomComposite on per-session SFU; template = student
 * /egress-recorder (student collapsed-tools + stage fullscreen for absentees).
 * Pause/continue = stop/start egress segments under one logical recording.
 */

import { livekitAuthService as authService } from "../utils/authService";

export type RecordingStatus =
  | "idle"
  | "recording"
  | "paused"
  | "stopped"
  | "starting"
  | "ending"
  | "complete"
  | "failed"
  | string;

export type RecordingSegment = {
  egress_id?: string;
  url?: string | null;
  s3_key?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  mode?: string | null;
  status?: string | null;
};

export type RecordingActionResult = {
  session_id: number;
  lms_session_id?: string;
  recording_id: string;
  status: RecordingStatus;
  url?: string | null;
  segments?: RecordingSegment[];
  extra?: {
    active_egress_id?: string | null;
    primary_url?: string | null;
    playback_urls?: string[];
    merged?: boolean;
    merge_pending?: boolean;
    merge_failed?: boolean;
    chapter_count?: number;
    chapter_segments?: RecordingSegment[];
    [key: string]: unknown;
  };
};

function logRecording(
  action: string,
  sessionId: string | number,
  result?: RecordingActionResult,
  err?: unknown,
) {
  if (err) {
    const data = (err as { response?: { data?: unknown; status?: number } })
      ?.response;
    console.error("[recording]", action, "failed", {
      sessionId,
      status: data?.status,
      data: data?.data,
      message: err instanceof Error ? err.message : String(err),
    });
    return;
  }
  console.info("[recording]", action, "ok", {
    sessionId,
    status: result?.status,
    recording_id: result?.recording_id,
    segments: result?.segments?.length ?? 0,
    extra: result?.extra,
  });
}

async function postRecordingAction(
  sessionId: string | number,
  action: "start" | "pause" | "continue" | "stop",
  body?: Record<string, unknown>,
): Promise<RecordingActionResult> {
  // ---------------------------------------------------------------
  // step 1: POST recording action to LMS LiveKit shim
  // ---------------------------------------------------------------
  console.info("[recording]", action, "request", { sessionId });
  try {
    const res = await authService.post(
      `/livekit/sessions/${sessionId}/recording/${action}`,
      body || {},
    );
    const result = res.data as RecordingActionResult;
    logRecording(action, sessionId, result);
    return result;
  } catch (err) {
    logRecording(action, sessionId, undefined, err);
    throw err;
  }
}

export async function startSessionRecording(
  sessionId: string | number,
): Promise<RecordingActionResult> {
  return postRecordingAction(sessionId, "start");
}

export async function pauseSessionRecording(
  sessionId: string | number,
): Promise<RecordingActionResult> {
  return postRecordingAction(sessionId, "pause");
}

export async function continueSessionRecording(
  sessionId: string | number,
): Promise<RecordingActionResult> {
  return postRecordingAction(sessionId, "continue");
}

export async function stopSessionRecording(
  sessionId: string | number,
  recordingId?: string,
): Promise<RecordingActionResult> {
  return postRecordingAction(sessionId, "stop", {
    recording_id: recordingId || "",
  });
}

export async function fetchSessionRecordingStatus(
  sessionId: string | number,
): Promise<RecordingActionResult> {
  // ---------------------------------------------------------------
  // step 1: GET logical recording status
  // ---------------------------------------------------------------
  console.info("[recording]", "status", "request", { sessionId });
  try {
    const res = await authService.get(`/livekit/sessions/${sessionId}/recording`);
    const result = res.data as RecordingActionResult;
    logRecording("status", sessionId, result);
    return result;
  } catch (err) {
    logRecording("status", sessionId, undefined, err);
    throw err;
  }
}
